/**
 * Output validation for the `prompt-execution` strategy (ADR 0005).
 *
 * A prompt-execution job carries a JSON Schema (`spec.outputSchema`). Validating
 * the agent's parsed response against it is intrinsic to the strategy — it is the
 * executor's job, not a workflow gate's. The primary guarantee should come from
 * the provider's structured-output mode (Anthropic tool schemas, OpenAI
 * `json_schema`); this is the universal post-hoc safety net for executors that
 * can't, or for providers that drift.
 *
 * Scope: a conservative validator over the jsonSchema7 subset that
 * `zod-to-json-schema` emits (type, properties, required, items, enum, const,
 * nested objects/arrays). It is deliberately **fail-closed on what it
 * understands and pass-through on what it doesn't**: an unrecognised construct
 * never produces a false rejection. We avoid a full JSON Schema dependency to
 * keep the bundled sifter binary lean and eval-free.
 */

import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';

export interface SchemaValidationResult {
  valid: boolean;
  errors: string[];
}

const JSON_TYPES = ['object', 'array', 'string', 'number', 'integer', 'boolean', 'null'] as const;
type JsonType = (typeof JSON_TYPES)[number];

function typeOf(value: unknown): JsonType {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  if (t === 'boolean') return 'boolean';
  if (t === 'string') return 'string';
  return 'object';
}

function matchesType(value: unknown, expected: string): boolean {
  const actual = typeOf(value);
  if (expected === 'number') return actual === 'number' || actual === 'integer';
  if (expected === 'integer') return actual === 'integer';
  return actual === expected;
}

function walk(value: unknown, schema: JSONSchema7Definition, path: string, errors: string[]): void {
  // Boolean schemas: `true` allows anything, `false` allows nothing.
  if (schema === true) return;
  if (schema === false) {
    errors.push(`${path || 'value'} is not allowed`);
    return;
  }

  // const / enum
  if (schema.const !== undefined && JSON.stringify(value) !== JSON.stringify(schema.const)) {
    errors.push(`${path || 'value'} must equal ${JSON.stringify(schema.const)}`);
  }
  if (Array.isArray(schema.enum) && !schema.enum.some((e) => JSON.stringify(e) === JSON.stringify(value))) {
    errors.push(`${path || 'value'} must be one of ${JSON.stringify(schema.enum)}`);
  }

  // type
  const types = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];
  if (types.length > 0 && !types.some((t) => matchesType(value, t))) {
    errors.push(`${path || 'value'} expected type ${types.join('|')}, got ${typeOf(value)}`);
    return; // a wrong type makes deeper checks meaningless
  }

  // object: required + per-property recursion
  if (typeOf(value) === 'object') {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(schema.required)) {
      for (const key of schema.required) {
        if (!(key in obj)) errors.push(`${path ? `${path}.` : ''}${key} is required`);
      }
    }
    if (schema.properties) {
      for (const [key, sub] of Object.entries(schema.properties)) {
        if (key in obj) walk(obj[key], sub, `${path ? `${path}.` : ''}${key}`, errors);
      }
    }
  }

  // array: per-item recursion against a single `items` schema
  if (typeOf(value) === 'array' && schema.items && !Array.isArray(schema.items)) {
    const itemSchema = schema.items;
    (value as unknown[]).forEach((item, i) => walk(item, itemSchema, `${path}[${i}]`, errors));
  }
}

/**
 * Validate a parsed value against a JSON Schema. Returns `{ valid, errors }`.
 * `null`/`undefined` is validated as `null` so a non-parseable response is
 * rejected by any schema that requires an object.
 */
export function validateAgainstSchema(value: unknown, schema: JSONSchema7): SchemaValidationResult {
  const errors: string[] = [];
  walk(value ?? null, schema as JSONSchema7Definition, '', errors);
  return { valid: errors.length === 0, errors };
}
